import React, { useEffect, useRef, createContext, useContext } from "react";

import { useSupabaseSession } from "~/context/SupabaseSessionContext";
import { initAnalytics } from "~/lib/analytics";

// AIDEV-NOTE: Analytics provider that initializes analytics with user context from Supabase session

const AnalyticsContext = createContext<{}>({});

export const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const { session } = useSupabaseSession();
    const isInitialized = useRef(false);

    // AIDEV-NOTE: Initialize analytics on client mount. Uses useEffect instead of useLayoutEffect
    // to avoid SSR warnings - analytics SDK init doesn't affect visible layout, so there's no need
    // for synchronous pre-paint timing.
    useEffect(() => {
        if (!isInitialized.current) {
            initAnalytics(session?.user);
            isInitialized.current = true;
        }
    }, [session?.user]);

    // AIDEV-NOTE: Update user identity when session changes (after initial mount)
    useEffect(() => {
        // initAnalytics handles the "already initialized" case by just updating identities
        if (session?.user && isInitialized.current) {
            initAnalytics(session.user);
        }
    }, [session?.user]);

    return (
        <AnalyticsContext.Provider value={{}}>
            {children}
        </AnalyticsContext.Provider>
    );
};

export const useAnalytics = () => {
    const context = useContext(AnalyticsContext);
    if (context === undefined) {
        throw new Error(
            "useAnalytics must be used within an AnalyticsProvider",
        );
    }
    return context;
};
